/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * Public /demo page — a 45-second auto-playing walkthrough that showcases
 * LUCRÜ in action. Designed to be shared as a link OR screen-recorded into
 * a social media clip. 4 scenes, auto-cycle, no interaction required.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LightningIcon,
  SparkleIcon,
  MicrophoneIcon,
  FireIcon,
  ChartBarIcon,
  TwitchLogoIcon,
  YoutubeLogoIcon,
  PlayIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const SCENES = [
  { id: "hero", duration: 7000 },
  { id: "chat", duration: 11000 },
  { id: "highlight", duration: 8000 },
  { id: "recap", duration: 8000 },
  { id: "cta", duration: 7000 },
];

const DEMO_MESSAGES = [
  { user: "pixel_queen", color: "#FF007F", text: "yo @streamer that aim is unreal 🔥" },
  { user: "kekw_42", color: "#39FF14", text: "LMAO you clutched that 1v3" },
  { user: "noob_destroyer", color: "#00E5FF", text: "lyra tell them to subscribe pls" },
  { user: "vibe_check", color: "#FFCA28", text: "best stream of the week fr" },
  { user: "newhere123", color: "#B88AFF", text: "just subbed 💜" },
];

const Visualizer = ({ active }) => {
  const bars = 28;
  return (
    <div className="flex items-center gap-1.5 h-16" aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => {
        const base = 8 + ((i * 37) % 50);
        if (!active) return <div key={i} style={{ height: 6, width: 4, borderRadius: 2, background: "#00E5FF55" }} />;
        return (
          <motion.div
            key={i}
            animate={{ height: [base, base + 40 + Math.random() * 24, base] }}
            transition={{ duration: 0.45 + (i % 5) * 0.08, repeat: Infinity, ease: "easeInOut", delay: i * 0.02 }}
            style={{
              width: 4, borderRadius: 2,
              background: "linear-gradient(180deg, #00E5FF, #39FF14 55%, #00E5FF22)",
              boxShadow: "0 0 12px #00E5FF66",
              height: base,
            }}
          />
        );
      })}
    </div>
  );
};

const DemoPage = () => {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [visibleMsgs, setVisibleMsgs] = useState([]);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const scene = SCENES[sceneIdx];

  // Scene auto-progression
  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => {
      setSceneIdx((i) => (i + 1) % SCENES.length);
    }, scene.duration);
    return () => clearTimeout(timerRef.current);
  }, [sceneIdx, paused]); // eslint-disable-line

  // Chat message rolling for scene "chat"
  useEffect(() => {
    if (scene.id !== "chat") { setVisibleMsgs([]); return; }
    setVisibleMsgs([]);
    const timers = DEMO_MESSAGES.map((m, i) => setTimeout(() => {
      setVisibleMsgs((prev) => [...prev, m]);
    }, 1500 + i * 1700));
    return () => timers.forEach(clearTimeout);
  }, [scene.id]);

  const skip = () => {
    clearTimeout(timerRef.current);
    setSceneIdx((i) => (i + 1) % SCENES.length);
  };

  const progress = useMemo(() => {
    return SCENES.map((_, i) => (i < sceneIdx ? 100 : i === sceneIdx ? 50 : 0));
  }, [sceneIdx]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#00E5FF]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#FF007F]/10 blur-3xl" />
        <div className="absolute top-1/3 left-1/2 w-[400px] h-[400px] rounded-full bg-[#39FF14]/8 blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <Logo size={32} withBackground />
          <span className="font-display font-black tracking-widest">LUCRÜ</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-2 py-0.5 ml-2">live demo</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPaused((p) => !p)}
            data-testid="demo-pause"
            className="text-xs text-white/50 hover:text-white"
          >
            {paused ? "▶ play" : "❚❚ pause"}
          </button>
          <Link to="/dashboard" data-testid="demo-cta-top" className="btn-pill btn-primary text-xs">
            Try it free <ArrowRightIcon size={12} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Progress bar */}
      <div className="relative z-10 flex gap-1 px-6">
        {progress.map((p, i) => (
          <div key={i} className="h-0.5 flex-1 bg-white/10 overflow-hidden rounded-full">
            <motion.div
              className="h-full bg-gradient-to-r from-[#00E5FF] to-[#39FF14]"
              initial={{ width: 0 }}
              animate={{ width: p === 100 ? "100%" : p === 50 ? "100%" : "0%" }}
              transition={{ duration: p === 50 ? scene.duration / 1000 : 0.3, ease: "linear" }}
              key={`${i}-${sceneIdx}`}
            />
          </div>
        ))}
      </div>

      {/* Scene stage */}
      <main className="relative z-10 flex items-center justify-center px-6 py-10 min-h-[70vh]" onClick={skip}>
        <AnimatePresence mode="wait">
          {scene.id === "hero" && (
            <motion.div
              key="hero"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl text-center"
            >
              <div className="text-xs text-[#00E5FF] uppercase tracking-[0.3em] mb-4">The AI co-host for live streamers</div>
              <h1 className="font-display font-black text-6xl md:text-7xl leading-[0.95] tracking-tight mb-6">
                Your chat, <span className="bg-gradient-to-r from-[#00E5FF] to-[#39FF14] bg-clip-text text-transparent">read out loud.</span>
              </h1>
              <p className="text-lg text-white/70 leading-relaxed max-w-xl mx-auto">
                You play. Lyra reads every message in a real human voice.
                No more squinting at the chat window.
              </p>
              <div className="flex items-center justify-center gap-6 mt-8 text-xs text-white/50">
                <span className="flex items-center gap-1.5"><TwitchLogoIcon size={14} /> Twitch</span>
                <span className="flex items-center gap-1.5"><YoutubeLogoIcon size={14} /> YouTube Live</span>
                <span>· Kick · 6 cam platforms</span>
              </div>
            </motion.div>
          )}

          {scene.id === "chat" && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
              className="w-full max-w-3xl"
            >
              <div className="glass rounded-2xl p-6 border border-[#222233]">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#39FF14] flex items-center justify-center">
                      <MicrophoneIcon size={18} color="#05050A" weight="fill" />
                    </div>
                    <div>
                      <div className="font-display font-black">Lyra · AI co-host</div>
                      <div className="text-[11px] text-[#39FF14] flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" /> Reading your chat
                      </div>
                    </div>
                  </div>
                  <Visualizer active={visibleMsgs.length > 0} />
                </div>
                <div className="space-y-2 min-h-[220px]">
                  <AnimatePresence>
                    {visibleMsgs.map((m, i) => (
                      <motion.div
                        key={i}
                        layout
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`flex items-baseline gap-2 text-sm px-3 py-2 rounded-lg ${
                          i === visibleMsgs.length - 1
                            ? "border border-[#00E5FF] bg-[#00E5FF]/5 shadow-[0_0_20px_rgba(0,229,255,0.2)]"
                            : "border border-transparent opacity-50"
                        }`}
                      >
                        <span className="font-semibold" style={{ color: m.color }}>{m.user}</span>
                        <span className="text-white/80">{m.text}</span>
                        {i === visibleMsgs.length - 1 && (
                          <span className="ml-auto text-[9px] text-[#00E5FF] uppercase tracking-wider">reading</span>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
              <div className="text-center mt-5 text-xs text-white/40">
                <SparkleIcon size={12} className="inline" /> 11+ ultra-realistic voices · smart-mode co-host adds witty one-liners
              </div>
            </motion.div>
          )}

          {scene.id === "highlight" && (
            <motion.div
              key="hl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl text-center"
            >
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block mb-6"
              >
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FF007F] to-[#FFCA28] flex items-center justify-center mx-auto shadow-[0_0_80px_rgba(255,0,127,0.6)]">
                  <FireIcon size={48} color="#fff" weight="fill" />
                </div>
              </motion.div>
              <div className="text-xs text-[#FF007F] uppercase tracking-[0.3em] mb-3">Hot moment captured</div>
              <h2 className="font-display font-black text-5xl leading-tight mb-4">Highlights auto-detected.</h2>
              <p className="text-white/70 max-w-md mx-auto">
                When your chat explodes, LUCRÜ marks the moment. Export the clip, post it anywhere, grow your audience.
              </p>
              <div className="flex items-center justify-center gap-3 mt-6 text-xs">
                <span className="px-3 py-1 rounded-full border border-[#FF007F]/40 text-[#FF007F]">BURST · 12 msgs / 20s</span>
                <span className="px-3 py-1 rounded-full border border-[#FFCA28]/40 text-[#FFCA28]">FUNNY · intensity 100</span>
              </div>
            </motion.div>
          )}

          {scene.id === "recap" && (
            <motion.div
              key="recap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="max-w-3xl w-full"
            >
              <div className="text-xs text-[#00E5FF] uppercase tracking-[0.3em] mb-3 text-center">weekly recap · one-tap share</div>
              <h2 className="font-display font-black text-4xl text-center mb-6">Your week with Lyra.</h2>
              <div className="glass rounded-2xl p-6 border border-[#222233] grid grid-cols-3 gap-4">
                {[
                  { v: "1,284", l: "msgs read aloud", c: "#00E5FF" },
                  { v: "4,619", l: "chat received", c: "#39FF14" },
                  { v: "28", l: "highlights", c: "#FF007F" },
                ].map((s) => (
                  <div key={s.l} className="text-center">
                    <div className="font-display font-black text-5xl" style={{ color: s.c }}>{s.v}</div>
                    <div className="text-xs text-white/60 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4 text-xs text-white/50">
                <ChartBarIcon size={12} className="inline" /> Auto-generated share card · one tap to Twitter / Instagram
              </div>
            </motion.div>
          )}

          {scene.id === "cta" && (
            <motion.div
              key="cta"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="text-center max-w-2xl"
            >
              <Logo size={64} withBackground />
              <h2 className="font-display font-black text-6xl leading-[0.95] tracking-tight mt-6 mb-4">
                Meet <span className="bg-gradient-to-r from-[#00E5FF] to-[#39FF14] bg-clip-text text-transparent">Lyra</span>.
              </h2>
              <p className="text-xl text-white/70 mb-8">
                30 days free · no card · one tap and you're live.
              </p>
              <Link
                to="/dashboard"
                data-testid="demo-cta-main"
                className="btn-pill btn-primary justify-center text-base px-8 py-3 inline-flex"
              >
                <PlayIcon size={16} weight="fill" /> Start reading my chat
              </Link>
              <div className="mt-6 text-[11px] text-white/40 uppercase tracking-widest">lucru.app</div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center text-[10px] text-white/30 py-4">
        LUCRÜ™ · © 2026 LUCRÜ · tap anywhere to skip · <Link to="/dashboard" className="underline hover:text-white/60">Open the app →</Link>
      </footer>
    </div>
  );
};

export default DemoPage;
